import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/core/components/ui/select';
import type { KioskLayoutOption } from '../../types/kiosk-flow-types';

interface LayoutSelectProps {
  value: string;
  layouts: KioskLayoutOption[];
  // Layouts picked by other rows — each XML can be used once
  usedLayouts: Set<string>;
  isLoading: boolean;
  // The value isn't in Asset Manager — only known once the list has loaded
  missing?: boolean;
  className?: string;
  disabled?: boolean;
  onChange: (layoutName: string) => void;
}

// Layout dropdown — one entry per XML name, with the screen sizes it exists in
export function LayoutSelect({
  value,
  layouts,
  usedLayouts,
  isLoading,
  missing,
  className,
  disabled,
  onChange,
}: LayoutSelectProps) {
  // A saved layout that isn't in the list still shows, so the row keeps its value
  const notListed = value !== '' && !layouts.some((l) => l.layoutName === value);
  const placeholder = isLoading ? 'Loading layouts…' : layouts.length === 0 ? 'No layouts' : 'Select layout';

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled || (isLoading && !value)}>
      <SelectTrigger className={`h-8 ${className ?? ''}`} aria-label="Layout">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {notListed && (
          <SelectItem value={value}>
            <span>{value}</span>
            {missing && (
              <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700">File missing</span>
            )}
          </SelectItem>
        )}
        {layouts.map((layout) => (
          <SelectItem
            key={layout.layoutName}
            value={layout.layoutName}
            disabled={layout.layoutName !== value && usedLayouts.has(layout.layoutName)}
          >
            <span>{layout.layoutName}</span>
            {layout.sizes.length > 0 && (
              <span className="ml-2 text-xs text-gray-400">{layout.sizes.join(' · ')}</span>
            )}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
